import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "sendButton", "status", "answer", "messages", "refreshButton", "results", "newSearch", "currentQuery", "suggestions", "history"]
  static values = { url: String, searchUrl: String, conversationId: Number }

  connect() {
    this.resetRequests()
    this.conversation = null
    this.pendingRequest = null
    this.query = this.inputTarget.value.trim()
    this.element.dataset.searchActive = String(!!this.query)
    this.loadedQuery = this.resultsTarget.dataset.query
    this.onPopState = () => this.restoreLocation()
    window.addEventListener("popstate", this.onPopState)
    this.onScroll = () => {
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => { if (this.restoreScroll == null) history.replaceState({ ...history.state, scroll: window.scrollY }, "") }, 200)
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.viewTurn = new URL(window.location.href).searchParams.get("turn")
    this.restorePage = history.state?.page || 1
    this.restoreScroll = history.state?.scroll
    if (this.conversationIdValue) {
      this.conversation = { id: this.conversationIdValue }
      this.refresh()
    }
  }

  disconnect() {
    this.statusTarget.dataset.loading = "false"
    window.removeEventListener("popstate", this.onPopState)
    window.removeEventListener("scroll", this.onScroll)
    clearTimeout(this.scrollTimer)
    this.abort.abort()
    this.resultsAbort?.abort()
    this.resultsAbort = null
    this.resultsLoading = null
    clearTimeout(this.timer)
  }

  resetRequests() {
    this.abort?.abort()
    this.resultsAbort?.abort()
    this.resultsAbort = null
    this.resultsLoading = null
    clearTimeout(this.timer)
    this.abort = new AbortController()
  }

  async request(path = "", options = {}) {
    const signal = this.abort.signal
    const response = await fetch(`${this.urlValue}${path}`, {
      credentials: "same-origin", ...options, signal,
      headers: { Accept: "application/json", "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
    })
    const data = response.status === 204 ? null : await response.json().catch(() => ({}))
    if (signal.aborted) throw new DOMException("Aborted", "AbortError")
    if (!response.ok) {
      const error = new Error(data.error || "request_failed")
      error.status = response.status
      throw error
    }
    return data
  }

  busy(value) {
    this.statusTarget.dataset.loading = String(value)
    this.sendButtonTarget.disabled = value
    this.answerTarget.setAttribute("aria-busy", String(value))
    this.suggestionsTarget.querySelectorAll("button[data-search-query]").forEach(button => { button.disabled = value })
  }

  showError(error) {
    if (error.name === "AbortError") return
    const messages = {
      allowance_reached: "The assistant’s usage limit has been reached. Here are matching results you can explore.",
      brief_changed: "This search changed. Check for an answer before trying again.",
      request_conflict: "Your search is still being processed. Check for an answer below.",
      assistant_unavailable: "An assistant answer is unavailable right now. You can browse matching content below.",
      invalid_request: "Please shorten your question and try again."
    }
    this.statusTarget.textContent = error.status === 401 ? "Sign in again for an assistant answer. Matching content is available below." :
      (messages[error.message] || "We couldn’t get an answer right now. You can browse matching content below.")
    this.busy(false)
    this.refreshButtonTarget.hidden = !this.conversation || (error.status >= 400 && error.status < 500 && ![409].includes(error.status))
    this.showFallback()
  }

  async ensureConversation() {
    if (this.conversation && !this.historical) return
    const body = this.historical ? { from_conversation: this.conversation.id, from_turn: this.viewTurn } : {}
    this.conversation = await this.request("", { method: "POST", body: JSON.stringify(body) })
    this.historical = false
    this.newSearchTarget.hidden = false
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.isComposing || event.keyCode === 229) return
    event.preventDefault()
    if (event.repeat || this.sendButtonTarget.disabled) return
    this.inputTarget.form.requestSubmit(this.sendButtonTarget)
  }

  async send(event) {
    event.preventDefault()
    const content = this.inputTarget.value.trim()
    if (!content || this.sendButtonTarget.disabled) return
    clearTimeout(this.scrollTimer)
    history.replaceState({ ...history.state, scroll: window.scrollY }, "")
    this.busy(true)
    this.element.dataset.searchActive = "true"
    this.statusTarget.textContent = "Searching HundrED…"
    this.query = content
    this.loadedQuery = null
    this.restorePage = 1
    this.restoreScroll = null
    this.resultsAbort?.abort()
    this.resultsAbort = null
    this.resultsLoading = null
    this.currentQueryTarget.textContent = content
    this.messagesTarget.replaceChildren()
    this.activeSources = []
    this.suggestionsTarget.replaceChildren()
    this.answerTarget.hidden = false
    this.resultsTarget.replaceChildren()
    this.loadResults(null, true)
    try {
      await this.ensureConversation()
      if (!this.pendingRequest || this.pendingRequest.content !== content) {
        this.pendingRequest = { content, input_mode: "chat", request_id: crypto.randomUUID(), lock_version: this.conversation.lock_version }
      }
      const submitted = await this.request(`/${this.conversation.id}/message`, { method: "POST", body: JSON.stringify({ search_message: this.pendingRequest }) })
      this.viewTurn = String(submitted.id)
      this.historical = false
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("conversation", this.conversation.id)
      url.searchParams.set("turn", this.viewTurn)
      history.pushState({ scroll: 0, page: 1 }, "", url.href)
      this.pendingRequest = null
      if (this.inputTarget.value.trim() === content) this.inputTarget.value = ""
      this.inputTarget.placeholder = "Ask a follow-up question…"
      this.deadline = Date.now() + 120000
      await this.loadConversation()
    } catch (error) {
      if (error.status >= 400 && error.status < 500) this.pendingRequest = null
      this.showError(error)
    }
  }

  async refresh() {
    if (!this.conversation) return
    this.resetRequests()
    this.deadline = Date.now() + 120000
    await this.loadConversation()
  }

  async restoreLocation() {
    this.resetRequests()
    this.pendingRequest = null
    this.inputTarget.value = ""
    this.busy(true)
    const url = new URL(window.location.href)
    this.viewTurn = url.searchParams.get("turn")
    this.restorePage = history.state?.page || 1
    this.restoreScroll = history.state?.scroll || 0
    this.loadedQuery = null
    this.resultsTarget.replaceChildren()
    this.messagesTarget.replaceChildren()
    this.activeSources = []
    this.suggestionsTarget.replaceChildren()
    this.query = url.searchParams.get("q") || ""
    const id = url.searchParams.get("conversation")
    if (!id) { window.location.reload(); return }
    this.conversation = { id }
    this.deadline = Date.now() + 120000
    await this.loadConversation()
  }

  async loadConversation() {
    try {
      const suffix = this.viewTurn ? `?turn=${encodeURIComponent(this.viewTurn)}` : ""
      const data = await this.request(`/${this.conversation.id}${suffix}`)
      this.conversation = data
      const historyLinks = (data.history || []).map(turn => {
        const link = document.createElement("a")
        const url = new URL(this.searchUrlValue, window.location.origin)
        url.search = new URLSearchParams({ conversation: data.id, turn: turn.id }).toString()
        link.href = url.href
        link.textContent = turn.title
        if (String(turn.id) === String(this.viewTurn)) link.setAttribute("aria-current", "page")
        return link
      })
      this.historyTarget.replaceChildren(...historyLinks)
      this.historical = !!(this.viewTurn && data.latest_turn_id && String(data.latest_turn_id) !== String(this.viewTurn))
      this.newSearchTarget.hidden = false
      const user = [...data.messages].reverse().find(item => item.role === "user")
      const latest = data.messages[data.messages.length - 1]
      if (!user) return
      this.viewTurn = String(user.id)
      this.query = user.content
      this.currentQueryTarget.textContent = user.content
      this.answerTarget.hidden = false
      this.element.dataset.searchActive = "true"
      this.renderMessages(latest?.role === "assistant" ? [latest] : [])
      const running = ["queued", "running"].includes(user.status)
      this.busy(running)
      this.refreshButtonTarget.hidden = !running || Date.now() < this.deadline
      if (running && Date.now() < this.deadline) {
        this.statusTarget.textContent = "Finding relevant results…"
        this.loadResults(null, true)
        this.timer = setTimeout(() => this.loadConversation(), 1500)
      } else if (running) {
        this.statusTarget.dataset.loading = "false"
        this.statusTarget.textContent = "Your answer is taking longer than expected. You can explore the results while you wait."
        this.loadResults(null, true)
      } else {
        this.statusTarget.textContent = user.status === "failed" || latest?.status === "failed" ? "An assistant answer isn’t available. Here are matching results." : ""
        this.loadResults()
      }
    } catch (error) { this.showError(error) }
  }

  showFallback() {
    if (this.query) this.loadResults(null, true)
  }

  resultPage(event) {
    const link = event.target.closest("a[data-search-page]")
    if (!link || !this.resultsTarget.contains(link)) return
    event.preventDefault()
    this.loadResults(Number(link.dataset.searchPage), this.resultsQuick)
  }

  async loadResults(page = null, quick = false) {
    const query = this.query
    const key = `${this.conversation?.id || ""}:${this.viewTurn || ""}:${query}:${quick}`
    if (!query || (page === null && this.loadedQuery === key)) return
    page ||= 1
    if (this.resultsLoading?.key === key && this.resultsLoading.page === page) return
    this.resultsAbort?.abort()
    const abort = new AbortController()
    this.resultsAbort = abort
    this.resultsLoading = { key, page }
    this.resultsTarget.setAttribute("aria-busy", "true")
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.search = new URLSearchParams({ q: query, page, results_only: "1" }).toString()
      if (quick) url.searchParams.set("quick", "1")
      else if (this.conversation && this.viewTurn) {
        url.searchParams.set("conversation", this.conversation.id)
        url.searchParams.set("turn", this.viewTurn)
      }
      const response = await fetch(url.href, { credentials: "same-origin", signal: abort.signal, headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error("results_unavailable")
      const html = await response.text()
      if (abort.signal.aborted) return
      this.renderResults(html, page > 1)
      this.resultsTarget.querySelectorAll(".search-fit-explanation").forEach(element => {
        const text = element.textContent
        element.replaceChildren()
        this.linkedText(element, text, this.activeSources || [])
      })
      this.loadedQuery = key
      this.resultsQuick = quick
      if (this.restorePage > page) { await this.loadResults(page + 1, quick); return }
      history.replaceState({ ...history.state, page }, "")
      if (this.restoreScroll != null) {
        const top = this.restoreScroll
        this.restoreScroll = null
        requestAnimationFrame(() => window.scrollTo(0, top))
      }
    } catch (error) {
      if (!abort.signal.aborted) {
        if (page > 1) this.statusTarget.textContent = "Couldn’t load more results. Your current results are still available; try Show more results again."
        else this.resultsTarget.textContent = "Matching content is temporarily unavailable. Please try again."
      }
    } finally {
      if (this.resultsAbort === abort) {
        this.resultsLoading = null
        this.resultsTarget.setAttribute("aria-busy", "false")
      }
    }
  }

  renderResults(html, append = false) {
    const parsed = new DOMParser().parseFromString(html, "text/html")
    if (!append) { this.resultsTarget.replaceChildren(...parsed.body.childNodes); return }
    const grid = this.resultsTarget.querySelector(".search-source-grid")
    if (!grid) { this.resultsTarget.replaceChildren(...parsed.body.childNodes); return }
    const identities = new Set([...grid.querySelectorAll("[data-search-identity]")].map(node => node.dataset.searchIdentity))
    parsed.querySelectorAll("[data-search-identity]").forEach(node => {
      if (!identities.has(node.dataset.searchIdentity)) { grid.append(node); identities.add(node.dataset.searchIdentity) }
    })
    this.resultsTarget.querySelector("nav")?.remove()
    const nav = parsed.querySelector("nav")
    nav?.querySelectorAll('[data-search-page]')?.forEach(link => { if (link.textContent === "Previous") link.remove() })
    if (nav) this.resultsTarget.append(nav)
    this.resultsTarget.querySelector(".search-result-count")?.remove()
  }

  sourceLink(source) {
    if (!source || typeof source.path !== "string" || !source.path.startsWith("/") || source.path.startsWith("//")) return null
    let url
    try { url = new URL(source.path, window.location.origin) } catch (_) { return null }
    if (url.origin !== window.location.origin) return null
    const link = document.createElement("a")
    link.href = url.href
    link.textContent = source.title
    return link
  }

  suggest(event) {
    const button = event.target.closest("button[data-search-query]")
    if (!button || !this.suggestionsTarget.contains(button) || this.sendButtonTarget.disabled) return
    this.inputTarget.value = button.dataset.searchQuery
    this.send(event)
  }

  linkedText(element, value, sources) {
    const text = String(value || "")
    // Only unambiguous, complete source titles become links. Never interpret HTML.
    const titles = new Map()
    sources.forEach(source => {
      const title = source.title?.trim()
      if (!title || title.length < 3 || !this.sourceLink(source)) return
      const key = title.toLocaleLowerCase()
      titles.set(key, titles.has(key) ? null : source)
    })
    const candidates = [...titles.values()].filter(Boolean).sort((a, b) => b.title.length - a.title.length)
    if (!candidates.length) { element.textContent = text; return }
    const escaped = candidates.map(source => source.title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    const pattern = new RegExp(escaped.join("|"), "giu")
    let offset = 0
    for (const match of text.matchAll(pattern)) {
      const start = match.index
      const end = start + match[0].length
      if (/[\p{L}\p{N}_]/u.test(text[start - 1] || "") || /[\p{L}\p{N}_]/u.test(text[end] || "")) continue
      const source = titles.get(match[0].toLocaleLowerCase())
      if (!source) continue
      element.append(document.createTextNode(text.slice(offset, start)))
      const link = this.sourceLink(source)
      link.textContent = match[0]
      element.append(link)
      offset = end
    }
    element.append(document.createTextNode(text.slice(offset)))
  }

  renderMessages(messages) {
    this.activeSources = messages.flatMap(message => message.answer?.sources || [])
    this.suggestionsTarget.replaceChildren()
    const nodes = messages.map(message => {
      const container = document.createElement("div")
      container.className = `search-message search-message--${message.role === "user" ? "user" : "assistant"}`
      const title = document.createElement("strong")
      title.textContent = message.role === "user" ? "You" : "HundrED answer"
      const text = document.createElement("p")
      const answer = message.answer || {}
      const sourceList = answer.sources || []
      if (message.role === "assistant") this.linkedText(text, message.content, sourceList)
      else text.textContent = message.content
      container.append(title, text)
      if (answer.clarification && !message.content?.includes(answer.clarification)) {
        const question = document.createElement("p")
        this.linkedText(question, answer.clarification, sourceList)
        container.append(question)
      }
      if (message.status === "failed") {
        const failure = document.createElement("p")
        const errors = {
          sources_changed: "Sources changed. Please search again.",
          request_interrupted: "This request was interrupted. Send a new message to try again.",
          provider_not_configured: "The assistant needs server configuration. Please contact the site administrator.",
          provider_authentication_failed: "The assistant could not authenticate with its AI provider. Please contact the site administrator.",
          provider_responses_permission_missing: "The AI provider has not granted permission to generate answers. Please contact the site administrator.",
          provider_model_request_permission_missing: "The AI provider has not granted permission to use the model. Please contact the site administrator.",
          provider_model_read_permission_missing: "The AI provider has not granted permission to read model information. Please contact the site administrator.",
          provider_key_rejected: "The AI provider rejected the assistant's credential. Please contact the site administrator.",
          provider_ip_denied: "The AI provider blocked this server's network access. Please contact the site administrator.",
          provider_organization_denied: "The AI provider denied organization or project access. Please contact the site administrator.",
          provider_permission_missing: "The AI provider has not granted the required permissions. Please contact the site administrator.",
          provider_access_denied: "The AI provider denied access. Please contact the site administrator.",
          provider_not_found: "The configured AI model or endpoint is unavailable. Please contact the site administrator.",
          provider_invalid_request: "The AI provider rejected the request configuration. Please contact the site administrator.",
          provider_quota_exceeded: "The AI provider's credits or usage limit have been reached. Please contact the site administrator.",
          provider_rate_limited: "The AI provider is receiving too many requests. Please wait before sending another message.",
          provider_connection_failed: "The connection to the AI provider failed. Please try again later.",
          provider_unavailable: "The AI provider is unavailable. Please try again later.",
          provider_incomplete_response: "The AI provider did not return a complete usable response. Please try a shorter request."
        }
        failure.textContent = errors[message.error_code] || "This request could not be completed. Please try again."
        container.append(failure)
      }
      const queries = (answer.suggested_queries || []).filter(query => typeof query === "string" && query.trim() && query.length <= 200).slice(0, 2)
      if (queries.length) {
        const suggestions = document.createElement("div")
        suggestions.className = "search-suggestions"
        const label = document.createElement("strong")
        label.textContent = "Explore further"
        suggestions.append(label)
        queries.forEach(query => {
          const button = document.createElement("button")
          button.type = "button"
          button.dataset.searchQuery = query
          button.textContent = query
          button.disabled = this.sendButtonTarget.disabled
          suggestions.append(button)
        })
        this.suggestionsTarget.replaceChildren(suggestions)
      }
      return container
    })
    this.messagesTarget.replaceChildren(...nodes)
  }
}
